<template>
  <div class="entry-rule-box">
    <v-row no-gutters>
      <v-col>
        <div data-test="entryRuleHeader" style="font-size: 16px">
          {{
            entryRule.name
              ? entryRule.name
              : $t("events:common.entryRules.ruleSet.heading", "Rule set") +
                " " +
                (ruleNumber || "")
          }}
        </div>
      </v-col>
      <v-col class="text-end">
        <v-btn-icon
          v-if="
            $allowedMutation(
              entryRule.allowedMutations,
              'competitionEntryRuleCreateOrUpdate'
            )
          "
          :to="{
            name: 'CompetitionRulesFormEdit',
            params: {
              competitionId: competition.id,
              competitionEntryRuleId: entryRule.id
            }
          }"
          data-test="editEntryRule"
          :aria-label="$t('common:editButton.alt')"
          size="small"
          icon
          class="bg-edit-green"
        >
          <v-icon class="text-white" size="small"> $edit </v-icon>
        </v-btn-icon>
        <v-btn-icon
          v-if="
            $allowedMutation(
              entryRule.allowedMutations,
              'competitionEntryRuleDelete'
            )
          "
          :aria-label="$t('common:removeButton.alt')"
          size="small"
          data-test="removeEntryRule"
          :loading="deleting"
          icon
          class="bg-remove"
          @click="entryRuleRemove($event)"
        >
          <v-icon class="text-white" size="small"> $iconTrash </v-icon>
        </v-btn-icon>
      </v-col>
    </v-row>

    <div
      v-for="(
        rule, index
      ) in entryRule.publicRegistrationConditionsDisplayTexts"
      :key="index"
      class="mb-2"
    >
      {{ displayText(rule) }}
    </div>
  </div>
</template>
<script>
import { defineComponent, ref } from "vue";
import gql from "fraql";
import { apolloClient } from "@/plugins/apollo";
import { useTranslation } from "i18next-vue";
import {
  useDisplayText,
  DisplayTextFragments
} from "@composables/display_text";
import {
  injectSnackbarError,
  injectAppConfirm
} from "@/composables/injections";

export default defineComponent({
  name: "CompetitionEntryRuleSet",
  props: {
    competition: {
      type: Object,
      required: true
    },
    entryRule: {
      type: Object,
      required: true
    },
    ruleNumber: {
      type: Number,
      default: undefined
    }
  },
  setup(props) {
    const { appConfirm } = injectAppConfirm();
    const { snackbarServerError } = injectSnackbarError();
    const { displayText } = useDisplayText();
    const { t } = useTranslation();

    const deleting = ref(false);

    async function entryRuleRemove() {
      const response = await appConfirm(
        t(
          "events:common.entryRules.ruleSet.deleteConfirmation.heading",
          "Delete rule set"
        ),
        t(
          "events:common.entryRules.ruleSet.deleteConfirmation.question",
          "You are going to delete the configured entry rule set for competition { add competition number }. After deletion these parameters will not be checked for past of future entries. Are your sure?"
        )
      );

      if (!response) return;

      deleting.value = true;

      try {
        await apolloClient.mutate({
          mutation: gql`
            mutation competitionEntryRuleDelete($id: ID) {
              competitionEntryRuleDelete(input: { id: $id }) {
                competition {
                  id
                }
              }
            }
          `,
          variables: {
            id: props.entryRule.id
          },
          update(cache) {
            const fieldRef = cache.identify({
              id: props.entryRule.id,
              __typename: "CompetitionEntryRule"
            });
            cache.modify({
              id: cache.identify({
                id: props.competition.id,
                __typename: "Competition"
              }),
              fields: {
                competitionEntryRules(existing = []) {
                  return existing.filter(x => x.__ref != fieldRef);
                }
              }
            });
          }
        });
      } catch (error) {
        snackbarServerError(error, { level: "error" });
      } finally {
        deleting.value = false;
      }
    }

    return {
      deleting,
      displayText,
      entryRuleRemove
    };
  },
  fragments: {
    competitionEntryRuleFields: gql`
      fragment _ on CompetitionEntryRule {
        id
        name
        publicRegistrationConditionsDisplayTexts {
          ${DisplayTextFragments.translation}
        }
        allowedMutations
      }
    `
  }
});
</script>
<style lang="scss">
.entry-rule-box {
  padding: 14px;
  border: 1px solid black;
  background-color: white;
  border-radius: 0.25em;
}
</style>
